/**
 * O documento em Markdown: o texto de volta para as mãos de quem escreveu.
 *
 * O Markdown é o **formato de ida e volta** do editor de documentos. Ele serve
 * para três coisas que o documento estruturado não faz sozinho:
 *
 * 1. **levar o texto para fora** — o `.md` abre em qualquer editor, entra no Git,
 *    vai por e-mail para o orientador e volta;
 * 2. **compartilhar sem perder o formato** — quem recebe vê `# título` e entende
 *    a estrutura, sem precisar do gerador;
 * 3. **continuar de onde parou** — colar de volta no gerador (`lerTextoColado`)
 *    reconstrói as mesmas seções, o resumo, as citações e a lista.
 *
 * A volta é o teste: o que sai daqui tem de voltar pelo mesmo caminho. Por isso
 * as peças da norma são escritas com o **rótulo que o leitor reconhece**
 * (`## RESUMO`, `## REFERÊNCIAS`, `Palavras-chave:`), e o **título do trabalho não
 * entra**: ele não é texto, mora na identificação, e um `#` no começo viraria uma
 * seção chamada "Maré alta" na volta.
 *
 * Uma coisa o `.md` **não** leva, de propósito: o **recuo de bloco** (os botões
 * "aumentar recuo"/"diminuir recuo"). Em Markdown, recuo é código ou citação —
 * escrever o recuo ali faria o leitor da volta inventar uma citação longa onde só
 * havia um parágrafo deslocado. O recuo é do **papel** (um degrau de 12,5 mm), e
 * quem volta pelo `.md` recebe o texto com a estrutura: a forma se refaz no editor.
 */
import { linhaDaReferencia, listaDaNorma, normaDoDocumento } from './normas.js';
import { blocosDaSecao, tabelaRetangular } from './blocos.js';
import { NIVEL_MAXIMO_DE_SECAO } from './tipos.js';
/**
 * O título de cada peça com campo próprio.
 *
 * São os rótulos que o leitor do texto colado já reconhece — e é por isso que o
 * `.md` volta para o gerador sem virar uma seção a mais no meio do trabalho.
 */
const TITULO_DA_PECA = {
    dedicatoria: 'DEDICATÓRIA',
    agradecimentos: 'AGRADECIMENTOS',
    epigrafe: 'EPÍGRAFE',
    apresentacao: 'APRESENTAÇÃO',
    epilogo: 'EPÍLOGO',
    sobreOAutor: 'SOBRE O AUTOR',
};
/** A ordem em que as peças são escritas — a mesma em que elas entram no papel. */
const ORDEM_DAS_PECAS = [
    'dedicatoria',
    'agradecimentos',
    'epigrafe',
    'apresentacao',
    'epilogo',
    'sobreOAutor',
];
/** Um bloco de texto em parágrafos: o que estava separado por linha em branco continua separado. */
function emParagrafos(texto) {
    return texto
        .split(/\n{2,}/)
        .map((bloco) => bloco.trim())
        .filter(Boolean);
}
/** O título da seção em Markdown, com o nível dela em `#` (até o sexto, como o editor). */
function tituloDaSecao(secao) {
    const nivel = Math.min(NIVEL_MAXIMO_DE_SECAO, Math.max(1, secao.nivel || 1));
    return `${'#'.repeat(nivel)} ${secao.titulo.trim()}`;
}
/** A citação longa em Markdown: cada bloco com o seu `>`. */
function citacaoEmMarkdown(citacao) {
    return citacao
        .split(/\n{2,}/)
        .map((bloco) => bloco
        .split('\n')
        .map((linha) => `> ${linha.trim()}`)
        .join('\n'))
        .filter((bloco) => bloco !== '>');
}
/**
 * Um bloco em Markdown.
 *
 * A lista sai com o marcador que ela imprime (`- ` ou `1. `), e é por isso que ela
 * **sobrevive à ida e volta**: colar o `.md` de volta no gerador reconstrói o bloco
 * de lista em vez de picar os itens em parágrafos soltos.
 */
/** Uma linha de tabela em Markdown: `| a | b |`. */
function linhaDeTabela(celulas) {
    return `| ${celulas.map((celula) => celula.replace(/\s+/g, ' ').replace(/\|/g, '\\|').trim()).join(' | ')} |`;
}
/**
 * A tabela em Markdown: os pipes, a legenda como título acima e a fonte embaixo.
 *
 * A legenda sai como **texto simples antes da tabela** e a fonte depois, com o
 * rótulo que a pessoa reconhece (`Legenda:` e `Fonte:`), porque é isso que o leitor
 * do texto colado sabe ler de volta — a sintaxe de pipes não tem onde guardá-las.
 */
function tabelaEmMarkdown(bloco) {
    const linhas = tabelaRetangular(bloco.linhas ?? []);
    if (linhas.length === 0)
        return [];
    const colunas = linhas[0]?.length ?? 0;
    if (colunas === 0)
        return [];
    const partes = [];
    if (bloco.legenda?.trim())
        partes.push(`Legenda: ${bloco.legenda.trim()}`);
    partes.push(linhaDeTabela(linhas[0]));
    partes.push(`| ${Array.from({ length: colunas }, () => '---').join(' | ')} |`);
    for (const linha of linhas.slice(1))
        partes.push(linhaDeTabela(linha));
    if (bloco.fonte?.trim())
        partes.push(`Fonte: ${bloco.fonte.trim()}`);
    return partes;
}
function blocoEmMarkdown(bloco, numeroDaNota) {
    /*
     * O recuo do bloco não sai daqui (ver o cabeçalho do módulo): o `.md` leva o
     * texto e a estrutura, e o recuo se refaz no editor.
     */
    if (bloco.tipo === 'TABELA')
        return tabelaEmMarkdown(bloco);
    if (bloco.tipo === 'IMAGEM') {
        const url = (bloco.url ?? '').trim();
        /* Imagem sem arquivo não vira linha: legenda sozinha não é figura. */
        if (!url)
            return [];
        const legenda = (bloco.legenda ?? '').replace(/\s+/g, ' ').trim();
        const linhas = [`![${legenda}](${url})`];
        if (bloco.fonte?.trim())
            linhas.push(`*Fonte: ${bloco.fonte.trim()}*`);
        return linhas;
    }
    if (bloco.tipo === 'NOTA') {
        /*
         * No texto a nota é o marcador `[^n]`, e o texto dela sai no bloco “Notas” do fim do
         * `.md` (ver `documentoEmMarkdown`): é assim que o Markdown escreve nota, e é o que faz
         * a nota sobreviver à leitura de volta.
         */
        return [`[^${numeroDaNota ?? ''}]`];
    }
    if (bloco.tipo === 'LISTA') {
        const itens = (bloco.itens ?? []).map((item) => item.replace(/\s+/g, ' ').trim()).filter(Boolean);
        if (itens.length === 0)
            return [];
        const numerada = bloco.lista === 'NUMERADA';
        return itens.map((item, indice) => (numerada ? `${indice + 1}. ${item}` : `- ${item}`));
    }
    const texto = bloco.texto?.trim();
    if (!texto)
        return [];
    if (bloco.tipo === 'CITACAO')
        return citacaoEmMarkdown(texto);
    return texto.split(/\n{2,}/).map((paragrafo) => paragrafo.trim()).filter(Boolean);
}
/**
 * O documento escrito em Markdown.
 *
 * Sai na ordem em que se lê um trabalho: resumo (com as palavras-chave), abstract,
 * as peças de abertura e fecho, as seções (com os parágrafos e as citações dentro)
 * e a lista de referências no fim.
 */
export function documentoEmMarkdown(documento) {
    const partes = [];
    if (documento.resumo.trim()) {
        partes.push('## RESUMO', ...emParagrafos(documento.resumo));
    }
    if (documento.palavrasChave.length > 0) {
        partes.push(`Palavras-chave: ${documento.palavrasChave.join('; ')}`);
    }
    if (documento.abstract.trim()) {
        partes.push('## ABSTRACT', ...emParagrafos(documento.abstract));
    }
    if (documento.keywords.length > 0) {
        partes.push(`Keywords: ${documento.keywords.join('; ')}`);
    }
    for (const item of documento.resumos ?? []) {
        if (!item.resumo.trim())
            continue;
        partes.push(`## ${(item.titulo.trim() || 'RESUMO').toUpperCase()}`, ...emParagrafos(item.resumo));
        if (item.palavrasChave.length > 0) {
            partes.push(`Palavras-chave: ${item.palavrasChave.join('; ')}`);
        }
    }
    for (const peca of ORDEM_DAS_PECAS) {
        const texto = documento[peca]?.trim();
        if (!texto)
            continue;
        partes.push(`## ${TITULO_DA_PECA[peca]}`, ...emParagrafos(texto));
    }
    /* A numeração das notas é do documento inteiro, na ordem em que elas aparecem. */
    let notas = 0;
    const textoDasNotas = [];
    for (const secao of documento.secoes) {
        /*
         * O texto sai dos **blocos** (e não de `paragrafos`): é o que faz a lista e a
         * citação longa chegarem ao `.md` no lugar em que estão no papel, e não todas
         * no fim da seção.
         */
        const conteudo = blocosDaSecao(secao).flatMap((bloco) => {
            if (bloco.tipo === 'NOTA') {
                notas += 1;
                if (bloco.texto?.trim())
                    textoDasNotas.push(`[^${notas}]: ${bloco.texto.trim()}`);
                return blocoEmMarkdown(bloco, notas);
            }
            return blocoEmMarkdown(bloco);
        });
        if (!secao.titulo.trim() && conteudo.length === 0)
            continue;
        /* Seção sem título (as que nascem do texto colado sem cabeçalho) não ganha `#`. */
        if (secao.titulo.trim())
            partes.push(tituloDaSecao(secao));
        partes.push(...conteudo);
    }
    if (documento.referencias.length > 0) {
        partes.push('## REFERÊNCIAS');
        /*
         * A lista sai na **ordem do papel** (alfabética, como a norma quer) e cada
         * linha formatada: o `.md` é o texto que a pessoa leva, então ele mostra o que
         * vai ser impresso. Referência `LIVRE` sai como foi escrita.
         */
        const norma = normaDoDocumento(documento);
        for (const item of listaDaNorma(documento.referencias, norma)) {
            /* A linha sai numerada quando a norma numera (`[1] …`, `1. …`). */
            const linha = linhaDaReferencia(item.referencia, norma, item.numero).trim();
            if (linha)
                partes.push(linha);
        }
    }
    /*
     * As notas por último, no bloco “NOTAS”: é onde a definição `[^n]:` do Markdown vai, e é
     * o que faz a nota de fim chegar inteira ao arquivo que a pessoa leva.
     */
    if (textoDasNotas.length > 0) {
        partes.push('## NOTAS', ...textoDasNotas);
    }
    return partes.length > 0 ? `${partes.join('\n\n')}\n` : '';
}
//# sourceMappingURL=markdown.js.map